import { useCallback, useEffect, useRef, useState } from "react";
import { Mic, Volume2, Info, Smartphone, PlayCircle, StopCircle, ShieldAlert } from "lucide-react";
import { logger } from "../lib/logger";

/**
 * Pre-call device check.
 *
 * Runs BEFORE the Daily.co frame is created — uses the browser's native `getUserMedia` +
 * `enumerateDevices` so the client & counsellor can:
 *   - Grant microphone permission early (so joining doesn't re-prompt them)
 *   - Pick their preferred mic and see a live audio meter reacting to their voice
 *   - Pick their preferred speaker and play a short test tone (piano-ish chime, ~1s)
 *   - Persist both selections to localStorage under the same keys the in-call DeviceSelector
 *     reads (`hf.dailyMicId`, `hf.dailySpeakerId`) so the choices carry into the Daily session
 *
 * Handles Safari gracefully: audiooutput enumeration is unavailable, so we show a helpful tip
 * instead of a broken dropdown.
 */
export default function PreCallCheck() {
  const [status, setStatus] = useState("idle"); // idle | requesting | ready | denied | error
  const [errorMsg, setErrorMsg] = useState("");
  const [mics, setMics] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const [selectedMic, setSelectedMic] = useState("");
  const [selectedSpeaker, setSelectedSpeaker] = useState("");
  const [level, setLevel] = useState(0); // 0..1 live mic RMS
  const [playing, setPlaying] = useState(false);

  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const toneRef = useRef({ osc: null, gain: null, stopTimer: null });
  const testAudioRef = useRef(null);

  const ios = typeof navigator !== "undefined" && /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const desktopSafari = typeof navigator !== "undefined"
    && /Safari/i.test(navigator.userAgent)
    && !/Chrome|Chromium|Edg|OPR/i.test(navigator.userAgent)
    && !ios;
  const supportsSpeakerPick = !ios && !desktopSafari && speakers.length > 0;

  const stopMicPipeline = useCallback(() => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => { try { t.stop(); } catch (e) { logger.debug(e?.message); } });
      streamRef.current = null;
    }
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch (e) { logger.debug(e?.message); }
      audioCtxRef.current = null;
    }
    analyserRef.current = null;
    setLevel(0);
  }, []);

  const startMicPipeline = useCallback(async (deviceId) => {
    stopMicPipeline();
    try {
      const constraints = { audio: deviceId ? { deviceId: { exact: deviceId } } : true, video: false };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      const AC = window.AudioContext || window.webkitAudioContext;
      const ctx = new AC();
      audioCtxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      src.connect(analyser);
      analyserRef.current = analyser;
      const buf = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteTimeDomainData(buf);
        // RMS over the wave — 0..1
        let sum = 0;
        for (let i = 0; i < buf.length; i += 1) {
          const v = (buf[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / buf.length);
        setLevel(Math.min(1, rms * 2.5)); // scale so normal speech ≈ 0.4-0.7
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
      return true;
    } catch (e) {
      logger.warn("getUserMedia failed:", e?.name || e?.message);
      if (e?.name === "NotAllowedError" || e?.name === "PermissionDeniedError") {
        setStatus("denied"); setErrorMsg("Microphone blocked. Enable it in your browser's site permissions and reload.");
      } else if (e?.name === "NotFoundError") {
        setStatus("error"); setErrorMsg("No microphone detected. Plug one in or check your OS sound settings.");
      } else {
        setStatus("error"); setErrorMsg(e?.message || "Could not access your microphone.");
      }
      return false;
    }
  }, [stopMicPipeline]);

  const enumerate = useCallback(async () => {
    try {
      const list = await navigator.mediaDevices.enumerateDevices();
      const nextMics = list.filter((d) => d.kind === "audioinput" && d.deviceId);
      const nextSpeakers = list.filter((d) => d.kind === "audiooutput" && d.deviceId);
      setMics(nextMics);
      setSpeakers(nextSpeakers);
      // Restore previous selection if it still exists on this machine
      let savedMic = "";
      let savedSpeaker = "";
      try {
        savedMic = localStorage.getItem("hf.dailyMicId") || "";
        savedSpeaker = localStorage.getItem("hf.dailySpeakerId") || "";
      } catch (e) { logger.debug(e?.message); }
      const micId = nextMics.find((m) => m.deviceId === savedMic)?.deviceId || nextMics[0]?.deviceId || "";
      const spkId = nextSpeakers.find((s) => s.deviceId === savedSpeaker)?.deviceId || nextSpeakers[0]?.deviceId || "";
      setSelectedMic(micId);
      setSelectedSpeaker(spkId);
      return { micId, spkId };
    } catch (e) {
      logger.warn("enumerateDevices failed:", e?.message);
      return { micId: "", spkId: "" };
    }
  }, []);

  const requestPermission = useCallback(async () => {
    setStatus("requesting"); setErrorMsg("");
    // First, request audio access with defaults — this unlocks device LABELS in enumerateDevices
    const initialOk = await startMicPipeline(undefined);
    if (!initialOk) return;
    const { micId } = await enumerate();
    // Now that we know which mic id we want, re-open the stream on it (labels resolved)
    if (micId) { await startMicPipeline(micId); }
    setStatus("ready");
  }, [startMicPipeline, enumerate]);

  const onMicChange = async (e) => {
    const id = e.target.value;
    setSelectedMic(id);
    try { localStorage.setItem("hf.dailyMicId", id); } catch (err) { logger.debug(err?.message); }
    await startMicPipeline(id);
  };

  const onSpeakerChange = async (e) => {
    const id = e.target.value;
    setSelectedSpeaker(id);
    try { localStorage.setItem("hf.dailySpeakerId", id); } catch (err) { logger.debug(err?.message); }
    // If a test tone is playing right now, retarget it to the new speaker immediately
    if (testAudioRef.current && typeof testAudioRef.current.setSinkId === "function" && id) {
      try { await testAudioRef.current.setSinkId(id); } catch (err) { logger.debug(err?.message); }
    }
  };

  const stopTestTone = useCallback(() => {
    const { osc, gain, stopTimer } = toneRef.current;
    if (stopTimer) { clearTimeout(stopTimer); }
    if (osc) { try { osc.stop(); } catch (e) { logger.debug(e?.message); } }
    if (gain) { try { gain.disconnect(); } catch (e) { logger.debug(e?.message); } }
    toneRef.current = { osc: null, gain: null, stopTimer: null };
    setPlaying(false);
  }, []);

  const playTestTone = useCallback(async () => {
    if (playing) { stopTestTone(); return; }
    try {
      // Create a fresh AudioContext for output. We keep the input pipeline's context separate
      // so stopping the tone doesn't kill the mic meter.
      const AC = window.AudioContext || window.webkitAudioContext;
      const ctx = new AC();
      const dest = ctx.createMediaStreamDestination();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = 523.25; // C5 — warm, friendly
      // Gentle envelope so it doesn't click
      const now = ctx.currentTime;
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.15, now + 0.05);
      gain.gain.setValueAtTime(0.15, now + 1.0);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 1.4);
      osc.connect(gain);
      gain.connect(dest);
      // Route through an <audio> element so we can honour the user's chosen speaker via setSinkId
      const el = document.createElement("audio");
      el.autoplay = true;
      el.srcObject = dest.stream;
      testAudioRef.current = el;
      if (supportsSpeakerPick && selectedSpeaker && typeof el.setSinkId === "function") {
        try { await el.setSinkId(selectedSpeaker); } catch (err) { logger.debug("setSinkId failed:", err?.message); }
      }
      osc.start();
      setPlaying(true);
      const stopTimer = setTimeout(() => {
        stopTestTone();
        try { ctx.close(); } catch (e) { logger.debug(e?.message); }
      }, 1500);
      toneRef.current = { osc, gain, stopTimer };
    } catch (e) {
      logger.warn("test tone failed:", e?.message);
      setErrorMsg("Could not play test tone.");
    }
  }, [playing, supportsSpeakerPick, selectedSpeaker, stopTestTone]);

  // Cleanup on unmount
  useEffect(() => () => { stopMicPipeline(); stopTestTone(); }, [stopMicPipeline, stopTestTone]);

  // Refresh device list if the OS reports a change (unplugging headphones etc.)
  useEffect(() => {
    if (status !== "ready" || !navigator.mediaDevices) return undefined;
    const handler = () => { enumerate(); };
    navigator.mediaDevices.addEventListener?.("devicechange", handler);
    return () => navigator.mediaDevices.removeEventListener?.("devicechange", handler);
  }, [status, enumerate]);

  // Auto-run permission request on mount — a proactive prompt is friendlier than an
  // opaque button that only says "click to start". Users who deny can retry via the button.
  useEffect(() => { requestPermission(); /* run once on mount */ }, [requestPermission]);

  // -------- render --------
  const barPct = Math.round(level * 100);
  const barSegments = 12;
  const litSegments = Math.round((barPct / 100) * barSegments);

  return (
    <div className="rounded-3xl border border-[#3D405B]/10 bg-white/70 backdrop-blur p-5 mb-4" data-testid="pre-call-check">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="text-lg font-extrabold text-[#3D405B]">Test your mic & speaker</h3>
          <p className="text-xs text-[#7A7D96] mt-0.5">Speak — you should see the bar move. Then tap the speaker to hear a short tone.</p>
        </div>
      </div>

      {status === "denied" && (
        <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-2xl p-3 mb-3" data-testid="permission-denied">
          <ShieldAlert size={16} className="shrink-0 mt-0.5" />
          <div>
            <div className="font-bold">Microphone access blocked</div>
            <div className="text-xs mt-1">{errorMsg}</div>
            <button
              type="button"
              onClick={requestPermission}
              className="mt-2 text-xs font-extrabold text-red-800 underline"
              data-testid="permission-retry-btn"
            >
              Try again
            </button>
          </div>
        </div>
      )}

      {status === "error" && (
        <div className="flex items-start gap-2 text-xs text-primary/80 bg-primary/5 border border-primary/20 rounded-2xl p-3 mb-3" data-testid="permission-error">
          <Info size={14} className="shrink-0 mt-0.5" /> {errorMsg}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* MIC */}
        <div className="rounded-2xl border border-[#3D405B]/10 bg-white/80 p-3" data-testid="pre-mic-block">
          <label htmlFor="pre-mic-select" className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.16em] text-[#3D405B] mb-2">
            <Mic size={14} className="text-primary" /> Microphone
          </label>
          {mics.length > 0 ? (
            <select
              id="pre-mic-select"
              value={selectedMic}
              onChange={onMicChange}
              className="w-full text-sm text-[#3D405B] font-semibold rounded-xl border border-[#3D405B]/15 bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/40 mb-3"
              data-testid="pre-mic-select"
            >
              {mics.map((d) => (
                <option key={d.deviceId} value={d.deviceId}>{d.label || `Mic (${d.deviceId.slice(0, 6)}…)`}</option>
              ))}
            </select>
          ) : (
            <p className="text-xs text-[#7A7D96] mb-3">{status === "requesting" ? "Detecting microphones…" : "No microphone detected"}</p>
          )}
          {/* Live level meter */}
          <div className="flex items-center gap-1" data-testid="mic-level-meter" aria-label={`Microphone level ${barPct}%`}>
            {Array.from({ length: barSegments }).map((_, i) => {
              const active = i < litSegments;
              const tone = i < 7 ? "bg-[#81B29A]" : i < 10 ? "bg-[#F2CC8F]" : "bg-[#E07A5F]";
              return (
                <span
                  key={i}
                  className={`h-4 flex-1 rounded-sm transition-all duration-75 ${active ? tone : "bg-[#3D405B]/10"}`}
                />
              );
            })}
          </div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#7A7D96] mt-1.5">{status === "ready" ? "Speak to test" : status === "requesting" ? "Waiting for permission…" : "Ready when you are"}</p>
        </div>

        {/* SPEAKER */}
        <div className="rounded-2xl border border-[#3D405B]/10 bg-white/80 p-3" data-testid="pre-speaker-block">
          <label htmlFor="pre-speaker-select" className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.16em] text-[#3D405B] mb-2">
            <Volume2 size={14} className="text-primary" /> Speaker
          </label>
          {supportsSpeakerPick ? (
            <select
              id="pre-speaker-select"
              value={selectedSpeaker}
              onChange={onSpeakerChange}
              className="w-full text-sm text-[#3D405B] font-semibold rounded-xl border border-[#3D405B]/15 bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/40 mb-3"
              data-testid="pre-speaker-select"
            >
              {speakers.map((d) => (
                <option key={d.deviceId} value={d.deviceId}>{d.label || `Speaker (${d.deviceId.slice(0, 6)}…)`}</option>
              ))}
            </select>
          ) : (
            <p className="text-xs text-[#7A7D96] mb-3 flex items-start gap-1.5">
              <Smartphone size={12} className="shrink-0 mt-0.5" />
              {ios ? "iOS uses the phone's speaker / connected headset automatically" : desktopSafari ? "Safari doesn't allow in-page speaker selection — use System Settings → Sound → Output" : "Using system default speaker"}
            </p>
          )}
          <button
            type="button"
            onClick={playTestTone}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-extrabold border border-[#3D405B]/15 bg-white hover:bg-[#F2CC8F]/20 transition"
            data-testid="pre-speaker-test-btn"
          >
            {playing ? <StopCircle size={14} /> : <PlayCircle size={14} />}
            {playing ? "Stop" : "Play test tone"}
          </button>
        </div>
      </div>
    </div>
  );
}
